import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Building2, Plus, Search, Loader2, X, Check, ArrowRight, LogOut, Mail, ShieldCheck, Inbox, UserCog,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgenciesPage() {
  const navigate = useNavigate();
  const [owned, setOwned] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [recruiting, setRecruiting] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", handle: "", description: "" });
  const [handleStatus, setHandleStatus] = useState(null);
  const [creating, setCreating] = useState(false);

  const [acceptInvite, setAcceptInvite] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);

  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  const load = useCallback(async () => {
    try {
      const [mineRes, invRes] = await Promise.all([
        axios.get(`${API}/agencies/mine`),
        axios.get(`${API}/agencies/invites/mine`),
      ]);
      setOwned(mineRes.data.owned || []);
      setMemberships(mineRes.data.memberships || []);
      setRecruiting(mineRes.data.recruiting || []);
      setInvites(invRes.data.invites || []);
    } catch { toast.error("Failed to load agencies"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const checkHandle = (val) => {
    setForm((f) => ({ ...f, handle: val }));
    setHandleStatus(val ? "checking" : null);
    clearTimeout(window.__agh);
    if (!val) return;
    window.__agh = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/agencies/handle-available?h=${encodeURIComponent(val)}`);
        setHandleStatus(r.data.available ? "ok" : (r.data.reason === "invalid" ? "invalid" : "taken"));
      } catch { setHandleStatus("invalid"); }
    }, 400);
  };

  const handleCreate = async () => {
    if (form.name.trim().length < 2) { toast.error("Agency name too short"); return; }
    if (handleStatus !== "ok") { toast.error("Pick a valid, available handle"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/agencies`, { ...form, handle: form.handle.toLowerCase() });
      toast.success("Agency created!");
      setShowCreate(false); setForm({ name: "", handle: "", description: "" }); setHandleStatus(null);
      navigate(`/app/agencies/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create agency"); }
    finally { setCreating(false); }
  };

  const doAccept = async () => {
    if (!consentChecked) { toast.error("Please tick the consent box"); return; }
    try {
      await axios.post(`${API}/agencies/invites/${acceptInvite.id}/accept`, { consent: true });
      toast.success(`Joined ${acceptInvite.agency_name}`);
      setAcceptInvite(null); setConsentChecked(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const acceptRecruiter = async (inv) => {
    try {
      await axios.post(`${API}/agencies/invites/${inv.id}/accept`, {});
      toast.success(`Joined ${inv.agency_name} as a recruiter`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const doDecline = async (inv) => {
    try { await axios.post(`${API}/agencies/invites/${inv.id}/decline`); toast.success("Declined"); load(); }
    catch { toast.error("Failed"); }
  };
  const doLeave = async (ag) => {
    if (!window.confirm(`Leave ${ag.name}? They'll stop receiving your stats.`)) return;
    try { await axios.post(`${API}/agencies/${ag.id}/leave`); toast.success("Left agency"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const doSearch = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults(null); return; }
    try { const r = await axios.get(`${API}/agencies/search?q=${encodeURIComponent(val.trim())}`); setResults(r.data.agencies || []); }
    catch { setResults([]); }
  };

  if (loading) return <div className="analytics-loading" data-testid="agencies-loading"><div className="processing-spinner" /></div>;

  return (
    <div className="page-container fade-in" data-testid="agencies-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Building2 size={24} /> Agencies</h1>
          <p style={{ color: "var(--text-secondary)" }}>Find an agency, manage your invites and memberships. Want to run one? Register as an agency from <span style={{ color: "#6366f1", cursor: "pointer" }} data-testid="agencies-to-settings" onClick={() => navigate("/app/settings")}>Settings</span>.</p>
        </div>
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div data-testid="agency-invites" style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><Inbox size={18} /> Invitations</h3>
          {invites.map((inv) => (
            <div key={inv.id} className="card" data-testid={`invite-${inv.agency_handle}`}
              style={{ padding: "1rem 1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, borderColor: "rgba(99,102,241,0.4)" }}>
              <div>
                <div style={{ fontWeight: 600 }}>{inv.agency_name} <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>@{inv.agency_handle}</span>
                  <span style={{ fontSize: "0.64rem", marginLeft: 6, padding: "1px 7px", borderRadius: 999, background: inv.role === "recruiter" ? "rgba(16,185,129,0.15)" : "rgba(99,102,241,0.15)", color: inv.role === "recruiter" ? "#10b981" : "#6366f1" }}>{inv.role === "recruiter" ? "recruiter" : "candidate"}</span>
                </div>
                <div style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>{inv.invited_by_name ? `Invited by ${inv.invited_by_name}` : "wants you to join"}{inv.role === "recruiter" ? " · lead a team (no personal stats shared)" : ""}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {inv.role === "recruiter"
                  ? <button className="btn-primary" data-testid={`invite-accept-${inv.agency_handle}`} onClick={() => acceptRecruiter(inv)}><Check size={14} /> Join as recruiter</button>
                  : <button className="btn-primary" data-testid={`invite-accept-${inv.agency_handle}`} onClick={() => { setAcceptInvite(inv); setConsentChecked(false); }}><Check size={14} /> Review & accept</button>}
                <button className="btn-secondary" data-testid={`invite-decline-${inv.agency_handle}`} onClick={() => doDecline(inv)}><X size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Directory search */}
      <div className="card" style={{ padding: "1.1rem", marginBottom: "1.4rem" }}>
        <label style={{ fontSize: "0.8rem", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><Search size={14} /> Agency directory</label>
        <input className="modal-input" data-testid="agency-search-input" value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Search agencies by name or @handle…" style={{ width: "100%" }} />
        {results !== null && (
          <div style={{ marginTop: 10 }} data-testid="agency-search-results">
            {results.length === 0 ? <div style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>No agencies found.</div>
              : results.map((a) => (
                <div key={a.id} style={{ padding: "0.6rem 0", borderTop: "1px solid var(--border-main)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div><div style={{ fontWeight: 500 }}>{a.name} <span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>@{a.handle} · {a.candidate_count} candidates</span></div>
                    {a.description && <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{a.description}</div>}</div>
                  {a.my_role === "admin" ? <span style={{ fontSize: "0.78rem", color: "#f59e0b" }}>You manage this</span>
                    : a.my_role === "candidate" ? <span style={{ fontSize: "0.78rem", color: "#10b981" }}>Member</span>
                    : <span style={{ fontSize: "0.76rem", color: "var(--text-muted)" }}>Invite-only</span>}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Owned */}
      {owned.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><ShieldCheck size={18} /> Agencies you manage</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
            {owned.map((a) => (
              <div key={a.id} className="card" data-testid={`owned-agency-${a.handle}`} onClick={() => navigate(`/app/agencies/${a.id}`)}
                style={{ padding: "1.3rem", cursor: "pointer" }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(245,158,11,0.15)", color: "#f59e0b", display: "flex", alignItems: "center", justifyContent: "center" }}><Building2 size={22} /></div>
                <h3 style={{ margin: "0.7rem 0 0.2rem" }}>{a.name}</h3>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>@{a.handle} · {a.candidate_count} candidate{a.candidate_count !== 1 ? "s" : ""}{a.pending_invites ? ` · ${a.pending_invites} pending` : ""}</div>
                <div style={{ marginTop: 12, color: "#6366f1", fontSize: "0.84rem", display: "flex", alignItems: "center", gap: 4 }}>Open dashboard <ArrowRight size={14} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recruiting (Team Head) */}
      {recruiting.length > 0 && (
        <div style={{ marginBottom: "1.5rem" }}>
          <h3 style={{ display: "flex", alignItems: "center", gap: 6 }}><UserCog size={18} /> Agencies you recruit for</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1rem" }}>
            {recruiting.map((a) => (
              <div key={a.id} className="card" data-testid={`recruiting-agency-${a.handle}`} onClick={() => navigate(`/app/agencies/${a.id}`)}
                style={{ padding: "1.3rem", cursor: "pointer" }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: "rgba(16,185,129,0.15)", color: "#10b981", display: "flex", alignItems: "center", justifyContent: "center" }}><UserCog size={22} /></div>
                <h3 style={{ margin: "0.7rem 0 0.2rem" }}>{a.name}</h3>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>@{a.handle} · {a.teams.length} team{a.teams.length !== 1 ? "s" : ""} you lead</div>
                <div style={{ marginTop: 12, color: "#6366f1", fontSize: "0.84rem", display: "flex", alignItems: "center", gap: 4 }}>Open recruiter view <ArrowRight size={14} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Memberships */}
      <h3>Your memberships</h3>
      {memberships.length === 0 ? (
        <div className="card" data-testid="memberships-empty" style={{ padding: "2rem", textAlign: "center", color: "var(--text-secondary)" }}>
          <Building2 size={36} style={{ opacity: 0.35 }} />
          <p>You're not a candidate in any agency. Agencies can invite you by your @username.</p>
        </div>
      ) : memberships.map((a) => (
        <div key={a.id} className="card" data-testid={`membership-${a.handle}`} style={{ padding: "1rem 1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ cursor: "pointer", flex: 1 }} data-testid={`open-membership-${a.handle}`} onClick={() => navigate(`/app/agencies/${a.id}`)}>
            <div style={{ fontWeight: 600 }}>{a.name} <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>@{a.handle}</span></div>
            <div style={{ fontSize: "0.78rem", color: "#10b981", display: "flex", alignItems: "center", gap: 4 }}><ShieldCheck size={13} /> View agency & your team</div>
          </div>
          <button className="btn-secondary" data-testid={`leave-agency-${a.handle}`} onClick={() => doLeave(a)} style={{ color: "#ef4444" }}><LogOut size={14} /> Leave</button>
        </div>
      ))}

      {/* Accept-with-consent modal */}
      {acceptInvite && (
        <div className="modal-overlay" data-testid="consent-modal" onClick={() => setAcceptInvite(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(460px, 100%)" }}>
            <h2 style={{ marginTop: 0 }}>Join {acceptInvite.agency_name}</h2>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>{acceptInvite.agency_description}</p>
            <label data-testid="consent-checkbox" onClick={() => setConsentChecked((v) => !v)}
              style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", padding: "0.9rem", borderRadius: 10, background: "var(--bg-surface-hl)", margin: "1rem 0" }}>
              <span style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, border: `1.5px solid ${consentChecked ? "#10b981" : "var(--border-main)"}`, background: consentChecked ? "#10b981" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {consentChecked && <Check size={14} color="#fff" />}
              </span>
              <span style={{ fontSize: "0.85rem" }}>{acceptInvite.consent_text}</span>
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-primary" data-testid="consent-accept-btn" onClick={doAccept} disabled={!consentChecked} style={{ flex: 1 }}><Check size={16} /> Accept & join</button>
              <button className="btn-secondary" onClick={() => setAcceptInvite(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" data-testid="create-agency-modal" onClick={() => setShowCreate(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: "1rem" }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ padding: "1.8rem", width: "min(460px, 100%)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h2 style={{ margin: 0 }}>Register an Agency</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
            </div>
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Agency name</label>
            <input className="modal-input" data-testid="agency-name-input" value={form.name} maxLength={80} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="CareerLaunch Agency" style={{ width: "100%", marginBottom: 12 }} />
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Handle (@)</label>
            <div style={{ position: "relative", marginBottom: 12 }}>
              <input className="modal-input" data-testid="agency-handle-input" value={form.handle} onChange={(e) => checkHandle(e.target.value.toLowerCase())} placeholder="careerlaunch" style={{ width: "100%", paddingRight: 34 }} />
              <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
                {handleStatus === "checking" && <Loader2 size={15} className="spin" />}
                {handleStatus === "ok" && <Check size={15} color="#10b981" data-testid="handle-ok" />}
                {(handleStatus === "taken" || handleStatus === "invalid") && <X size={15} color="#ef4444" data-testid="handle-bad" />}
              </span>
            </div>
            <label style={{ fontSize: "0.82rem", color: "var(--text-secondary)" }}>Description</label>
            <textarea className="modal-input" data-testid="agency-desc-input" value={form.description} rows={2} maxLength={300} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does your agency do?" style={{ width: "100%", marginBottom: 16, resize: "vertical" }} />
            <button className="btn-primary" data-testid="create-agency-submit" onClick={handleCreate} disabled={creating} style={{ width: "100%" }}>
              {creating ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} Register Agency
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
